import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';
import { useParams } from 'react-router-dom';

import Content from './Content';
import Actions from './Actions';

import ProductResource from 'resources/organization/ProductResource';
import EventResource from 'resources/organization/EventResource';

import { useError } from 'utils/useErrorController';
import { useToast } from 'utils/context/ToastContext';
import { offerCategory, visibilityOptions } from 'utils/offersFormHelper';
import { validateStringField } from 'utils/validations/validateStringField';
import { convertPriceToCents } from 'utils/rewards';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const defaultTicket = {
  title: '',
  description: '',
  supply_limit: '',
  price: '',
  visible: visibilityOptions[0].value,
  status: "active",
  category: offerCategory.ticket.value,
  points: 0,
};

const RecurrentEventTicketModal = ({ open, onClose, event, onEventChange }) => {
  const [newTicket, setNewTicket] = useState(defaultTicket);

  const { fetch } = useController();
  const { handleError, setLoading, loading } = useError();
  const { showToast } = useToast();
  const { organizationId, eventId } = useParams();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewTicket(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      validateStringField(newTicket.title, "a valid title");
      
      const ticketData = {
        ...newTicket,
        price: newTicket.price ? convertPriceToCents(newTicket.price) : null,
        event_id: event.id,
      };

      await fetch(ProductResource.create(), 
        { organization_id: organizationId }, 
        ticketData
      );

      const updatedEvent = await fetch(
        EventResource.detail(), 
        { id: eventId, organization_id: organizationId }
      );
      
      onEventChange(updatedEvent);
      
      showToast('success', 'Ticket created and added to all sessions');
      
      handleClose();
    } catch (error) {
      handleError(error);
    }
  };

  const handleClose = () => {
    setLoading(false);
    setNewTicket(defaultTicket);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Add New Ticket
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 12,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Content ticket={newTicket} onChange={handleChange} />

      <Actions 
        onClose={handleClose} 
        onSubmit={handleSave} 
        loading={loading}
        disabled={!newTicket.title}
      />
    </Dialog>
  );
};

RecurrentEventTicketModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
  onEventChange: PropTypes.func.isRequired,
};

export default RecurrentEventTicketModal;

